import type { UseQueryResult } from "@tanstack/react-query";
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Catalog, Thread } from "@/entities/thread";
import { DesktopCatalogGridLayout } from "@/features/catalog/components/desktop/DesktopCatalogGridLayout";
import { DesktopCatalogItem } from "@/features/catalog/components/desktop/DesktopCatalogItem";
import { useFavoriteThreads } from "@/features/catalog/hooks/useFavoriteThreads";
import { useFilteredThreads } from "@/features/catalog/hooks/useFilteredThreads";
import { useOekakiFloor } from "@/features/catalog/hooks/useOekakiFloor";
import type { CatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import {
  type CatalogWheelEvent,
  createCatalogWheelMediator,
  isCatalogWheelEvent,
} from "@/features/catalog/mediators/catalogWheelMediator";
import {
  createCatalogContext,
  getLastCatalogIds,
  getNewCatalogThreadIds,
  useCatalogStore,
} from "@/features/catalog/stores/catalogStore";
import { catalogContextBoardSlug } from "@/features/catalog/utils/catalogContext";
import type { EventNode, TypedEvent } from "@/shared/lib/componentHierarchy";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
} from "@/shared/ui/component-hierarchy/hooks";
import { Message } from "@/shared/ui/feedback";
import { QueryStateBoundary } from "@/shared/ui/feedback/QueryStateBoundary";
import { ModalContext } from "@/shared/ui/overlay/modal-context";

interface DesktopCatalogGridProps {
  query: UseQueryResult<Catalog>;
  wheelReloadEnabled?: boolean;
}

export const DesktopCatalogGrid: React.FunctionComponent<
  DesktopCatalogGridProps
> = ({ query, wheelReloadEnabled = true }: DesktopCatalogGridProps) => {
  const { data, error, isFetching, isLoading } = query;
  const { isModalOpen } = useContext(ModalContext);
  const nodeRef = useRef<EventNode<TypedEvent> | null>(null);
  const [wheelMediator] = useState(() =>
    createCatalogWheelMediator(wheelReloadEnabled && !isModalOpen, (effect) => {
      nodeRef.current?.dispatch(effect);
    }),
  );
  useHierarchyLifetime(wheelMediator);
  const node = useComponentNode((event) =>
    isCatalogWheelEvent(event) &&
    wheelMediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled",
  );
  nodeRef.current = node;
  useLayoutEffect(() => {
    wheelMediator.dispatch({
      type: "catalog-wheel.enabled",
      enabled: wheelReloadEnabled && !isModalOpen,
    });
  }, [wheelMediator, wheelReloadEnabled, isModalOpen]);
  const filteredThreads = useFilteredThreads(data?.threads);
  const visibleThreads = useFavoriteThreads(useOekakiFloor(filteredThreads));
  const lastCatalogIds = useCatalogStore((state) => state.lastCatalogIds);
  const currentSort = useCatalogStore((state) => state.currentSort);
  const page = useCatalogStore((state) => state.page);
  const columns = useCatalogStore((state) => state.columns);
  const imageSize = useCatalogStore((state) => state.imageSize);
  const newThreadIds = useMemo(() => {
    const context = createCatalogContext(
      catalogContextBoardSlug(),
      page,
      currentSort,
    );
    return getNewCatalogThreadIds(
      data?.threads.map((thread) => thread.id) ?? [],
      getLastCatalogIds({ lastCatalogIds }, context),
    );
  }, [currentSort, data?.threads, lastCatalogIds, page]);

  return (
    <ComponentScope node={node}>
      <DesktopCatalogGridView
        error={error}
        hasData={data !== undefined}
        isLoading={isLoading}
        isRetrying={isFetching}
        visibleThreads={visibleThreads}
        newThreadIds={newThreadIds}
        columns={columns}
        imageSize={imageSize}
        wheelReloadEnabled={wheelReloadEnabled}
        isModalOpen={isModalOpen}
        onEvent={node.dispatch}
      />
    </ComponentScope>
  );
};

export interface DesktopCatalogGridViewProps {
  readonly error: Error | null;
  readonly hasData: boolean;
  readonly isLoading: boolean;
  readonly isRetrying: boolean;
  readonly visibleThreads: readonly Thread[];
  readonly newThreadIds: ReadonlySet<string>;
  readonly columns: number;
  readonly imageSize: number;
  readonly wheelReloadEnabled: boolean;
  readonly isModalOpen: boolean;
  readonly onEvent: (event: CatalogEvent | CatalogWheelEvent) => void;
}

export function DesktopCatalogGridView({
  error,
  hasData,
  isLoading,
  isRetrying,
  visibleThreads,
  newThreadIds,
  columns,
  imageSize,
  wheelReloadEnabled,
  isModalOpen,
  onEvent,
}: DesktopCatalogGridViewProps): React.ReactElement {
  useEffect(() => {
    if (!wheelReloadEnabled || isModalOpen) {
      return;
    }
    const wheel = (event: WheelEvent): void => {
      const atEdge =
        event.deltaY < 0
          ? window.scrollY <= 5
          : window.scrollY + window.innerHeight >=
            document.documentElement.scrollHeight - 5;
      onEvent({
        type: "catalog-wheel.observed",
        deltaY: event.deltaY,
        deltaMode: event.deltaMode,
        atEdge,
        now: performance.now(),
        hasModifier:
          event.shiftKey || event.altKey || event.ctrlKey || event.metaKey,
      });
    };
    const resetWheelState = (): void => {
      onEvent({ type: "catalog-wheel.scroll", now: performance.now() });
    };
    window.addEventListener("wheel", wheel, { passive: true });
    window.addEventListener("scroll", resetWheelState, { passive: true });
    return (): void => {
      window.removeEventListener("wheel", wheel);
      window.removeEventListener("scroll", resetWheelState);
    };
  }, [isModalOpen, onEvent, wheelReloadEnabled]);

  return (
    <QueryStateBoundary
      error={error}
      hasData={hasData}
      isLoading={isLoading}
      isRetrying={isRetrying}
      message="スレッド一覧の読み込みに失敗しました"
      onRetry={(): void => {
        const catalogEvent: CatalogEvent = {
          type: "catalog/retry-requested",
        };
        onEvent(catalogEvent);
      }}
    >
      {visibleThreads.length === 0 ? (
        <Message variant="info">スレッドが見つかりません</Message>
      ) : (
        <DesktopCatalogGridLayout columns={columns} imageSize={imageSize}>
          {visibleThreads.map((thread) => (
            <DesktopCatalogItem
              key={thread.id}
              thread={thread}
              isNew={newThreadIds.has(thread.id)}
            />
          ))}
        </DesktopCatalogGridLayout>
      )}
    </QueryStateBoundary>
  );
}
